import React from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { salonData } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const Contact = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[#f7f5f2]">
      <SEO page="contact" />
      {/* Header */}
      <div className="bg-white border-b border-[#5f9dff]/20">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-[#1e1919] mb-4">
            {t.contact.title}
          </h1>
          <p className="text-xl text-[#736c64] max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12">
          {/* Contact Information */}
          <div className="space-y-4 sm:space-y-6">
            <Card className="border-[#5f9dff]/20 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-4 sm:p-8">
                <a
                  href="https://www.google.com/maps/place/Madame+Cristina/@55.8621583,9.8536466,17.25z/data=!4m6!3m5!1s0xa29590414ae9c2f1:0xf2e3308c297c6031!8m2!3d55.8625682!4d9.8546485!16s%2Fg%2F11v0xc6f4j?entry=ttu&g_ep=EgoyMDI1MTEyMy4xIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start group cursor-pointer"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#61525a] rounded-full flex items-center justify-center mr-4 sm:mr-6 flex-shrink-0 group-hover:bg-[#4a3e44] transition-colors">
                    <MapPin className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-2">{t.contact.address}</h3>
                    <p className="text-base sm:text-lg text-[#736c64] group-hover:text-[#5f9dff] transition-colors break-words">{salonData.contact.address}</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            <Card className="border-[#5f9dff]/20 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-4 sm:p-8">
                <a
                  href={`tel:${salonData.contact.phone.replace(/\s/g, '')}`}
                  className="flex items-start group cursor-pointer"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#5f9dff] rounded-full flex items-center justify-center mr-4 sm:mr-6 flex-shrink-0 group-hover:bg-[#4a8fef] transition-colors">
                    <Phone className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-2">{t.contact.phone}</h3>
                    <p className="text-base sm:text-lg text-[#736c64] group-hover:text-[#5f9dff] transition-colors break-words">{salonData.contact.phone}</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            <Card className="border-[#5f9dff]/20 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-4 sm:p-8">
                <a
                  href={`mailto:${salonData.contact.email}`}
                  className="flex items-start group cursor-pointer"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#61525a] rounded-full flex items-center justify-center mr-4 sm:mr-6 flex-shrink-0 group-hover:bg-[#4a3e44] transition-colors">
                    <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-2">{t.contact.email}</h3>
                    <p className="text-base sm:text-lg text-[#736c64] group-hover:text-[#5f9dff] transition-colors break-words">{salonData.contact.email}</p>
                  </div>
                </a>
              </CardContent>
            </Card>

            <Card className="border-[#5f9dff]/20 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-4 sm:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-[#1e1919] mb-3 sm:mb-4">Follow Me</h3>
                <p className="text-sm sm:text-base text-[#736c64] mb-4 sm:mb-6">Stay updated with my latest work and styling inspiration</p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a
                    href="https://www.instagram.com/madamecristinahair"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <Instagram className="w-5 h-5" strokeWidth={2} />
                    <span className="font-semibold text-sm sm:text-base">Instagram</span>
                  </a>
                  <a
                    href="https://www.facebook.com/madamecristina"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#1877f2] hover:bg-[#0c5dcd] text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    <Facebook className="w-5 h-5" strokeWidth={2} />
                    <span className="font-semibold text-sm sm:text-base">Facebook</span>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Hours */}
          <div>
            <Card className="border-[#5f9dff]/20 h-full">
              <CardContent className="p-4 sm:p-8">
                <div className="flex items-center mb-6 sm:mb-8">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#5f9dff] rounded-full flex items-center justify-center mr-4 sm:mr-6">
                    <Clock className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1e1919]">{t.contact.openingHours}</h3>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between items-center py-3 sm:py-4 border-b border-[#5f9dff]/20">
                    <span className="text-base sm:text-lg font-semibold text-[#1e1919]">
                      {t.days.monday}
                    </span>
                    <span className="text-sm sm:text-lg text-[#736c64] text-right">
                      14:00 - 20:00
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 sm:py-4 border-b border-[#5f9dff]/20">
                    <span className="text-base sm:text-lg font-semibold text-[#1e1919]">
                      {t.days.tuesday} - {t.days.friday}
                    </span>
                    <span className="text-sm sm:text-lg text-[#736c64] text-right">
                      09:00 - 20:00
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 sm:py-4 border-b border-[#5f9dff]/20">
                    <span className="text-base sm:text-lg font-semibold text-[#1e1919]">
                      {t.days.saturday}
                    </span>
                    <span className="text-sm sm:text-lg text-[#736c64] text-right">
                      09:00 - 15:00
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-3 sm:py-4">
                    <span className="text-base sm:text-lg font-semibold text-[#1e1919]">
                      {t.days.sunday}
                    </span>
                    <span className="text-sm sm:text-lg text-[#736c64] text-right">
                      {t.days.closed}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Map */}
        <div className="mt-12">
          <Card className="border-[#5f9dff]/20 overflow-hidden">
            <CardContent className="p-0">
              <div style={{ width: '100%' }}>
                <iframe
                  width="100%"
                  height="600"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=Kattesund%2019+(Madame%20Cristina)&amp;t=&amp;z=16&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                  title="Madame Cristina Location"
                ></iframe>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contact;